import { useState, useEffect, useRef } from "react";
import { FeedbackModal } from "@/components/FeedbackModal";
import { MessageSquare } from "lucide-react";
import { useLocation } from "wouter";
import { useIsMobile } from "../hooks/use-mobile";

const FloatingFeedbackButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const isMobile = useIsMobile();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll-aware visibility for mobile
  useEffect(() => {
    if (!isMobile) {
      setIsVisible(true);
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Hide on scroll down, show on scroll up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);

      // Always show after scrolling stops
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [lastScrollY, isMobile]);

  // Only show on mobile
  if (!isMobile) return null;

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={`fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#EE7D79] text-white shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'
        }`}
        aria-label="Give Feedback"
        style={{ backgroundColor: '#EE7D79' }}
      >
        <MessageSquare className="h-6 w-6" />
      </button>
      <FeedbackModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </>
  );
};

export default FloatingFeedbackButton;
